'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useResumePendingObservingVideoUploads } from '@/features/visit-counsel/moving-items-flow/model/useResumePendingObservingVideoUploads';
import { useObservingSessionPendingActions } from '@/features/visit-counsel/room-observing-flow/model/pendingObservingSessions';
import useSWR from 'swr';
import { TabPageComponent } from '@/entities/tab/model/tab.types';
import type { TabContext } from '@/entities/tab/model/tab.types';
import { useCounselPageContext } from '@/features/visit-counsel/counsel-page-context';
import {
  CounselPageShell,
  CounselLoadingPlaceholder,
} from '@/features/visit-counsel/counsel-page-layout';
import { useMyVisitCounselDetail } from '@/entities/visit-counsel';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import { CounselStartFlowAdapter } from '@/features/visit-counsel/counsel-start-flow';
import {
  type CounselStartFlowContext,
  buildDeparturePrefill,
} from '@miso/counsel-schema';
import {
  reviewEditSubStage,
  type ReviewSection,
} from '@miso/counsel-schema';
import { HouseStructureFlowAdapter } from '@/features/visit-counsel/house-structure-flow';
import { MovingItemsFlowAdapter } from '@/features/visit-counsel/moving-items-flow';
import { ProgressFlowAdapter } from '@/features/visit-counsel/progress-flow';
import { RoomObservingFlowAdapter } from '@/features/visit-counsel/room-observing-flow';
import {
  EditTargetSelector,
  EditObservingFlow,
  type EditTarget,
} from '@/features/visit-counsel/room-observing-flow';
import { AdditionalSpaceFlowAdapter } from '@/features/visit-counsel/additional-space-flow';
import { SketchVideoStepAdapter } from '@/features/visit-counsel/sketch-video-flow';
import {
  CounselReviewScreen,
  type ReviewEditRoomInfo,
  type ReviewEditAdditionalSpaceInfo,
} from '@/features/visit-counsel/counsel-review';
import {
  resolveCounselFlowStage,
  type CounselFlowStage,
} from '@/features/visit-counsel/counsel-flow-stage';
import type { ProgressSubStage } from '@/features/visit-counsel/counsel-flow-stage/model/resolveCounselFlowStage';
import {
  CounselCompleteScreen,
  isCounselReadyForCompletion,
} from '@/features/visit-counsel/counsel-complete';
import { deserialize } from '@/entities/tab/model/path';
import type { MovingItemRequest } from '@/entities/visit-counsel/model/room-observing.types';

/**
 * 통합 InquiryFlow 진입 페이지 — `/workspace/my/counsels/{id}/start`.
 *
 * 한 페이지가 상담 진행의 모든 단계(주소·방 구조·짐 기록·진행 sub·스케치 영상·추가공간 촬영)
 * 를 어댑터로 이어서 처리한다. 단계 결정은 `resolveCounselFlowStage(counsel)`이 담당하며,
 * 각 어댑터의 콜백은 refetch만 트리거하면 충분하다 — 새 counsel이 도착하면
 * 본 페이지가 자동으로 다음 단계 어댑터를 렌더한다.
 *
 *   APPOINTED                          → CounselStartFlowAdapter
 *   STARTED + sketching 없음           → HouseStructureFlowAdapter
 *   STARTED + 미완료 방/추가공간 존재  → MovingItemsFlowAdapter / RoomObservingFlowAdapter
 *   모든 방 짐 기록 완료                → ProgressFlowAdapter (sub-stage 자동 분기:
 *                                         이사일자/출발지·도착지 운반/주소/포장/이사유형/요청)
 *   진행 sub-stage 모두 완료
 *     ├─ post-progress 리뷰(1회) → CounselReviewScreen (CTA "다음")
 *     │                              CTA 클릭 → `completeIntakeSession` API 호출
 *     │                              (백엔드가 운반방식 기반으로 추가공간 세션 자동 생성)
 *     │                              → refetch → reviewedPostProgress=true → 다음 stage 진입
 *     ├─ sketch-video → SketchVideoStepAdapter (집 스케치 영상)
 *     └─ additional-spaces → AdditionalSpaceFlowAdapter (백엔드 자동 생성된
 *                                  LADDER_TRUCK_ENTRANCE/STAIRS/RESIDENCE_STORAGE 등)
 *   모두 완료(stage='done')
 *     ├─ "상담 완료 조건" 충족          → CounselCompleteScreen (자동 종료 + 축하 화면)
 *     └─ 충족되지 않음                  → CounselReviewScreen 노출 (현황 표시 + "상담 상세에서
 *                                         마무리하기" CTA. 옛 토스트 + auto-bounce 동작은 사용자가
 *                                         "튕김" 으로 인지해 진행 불가 회귀를 만들었기에 제거).
 */
export const CounselStartPage: TabPageComponent = (props: TabContext) => {
  const {
    showToast,
    navigateTab,
    isAuthInitialized,
    isAuthLoading,
    plannerId,
    isPlannerMode,
    isMobile,
  } = useCounselPageContext({ tabId: props.tabId ?? '' });
  const tabId = props.tabId;
  const visitCounselId = props.path?.[2] ?? '';

  const { data, isLoading, error, refetch, mutateWithCounsel } = useMyVisitCounselDetail(
    visitCounselId,
    isAuthInitialized && !isAuthLoading && !!visitCounselId && !!plannerId && isPlannerMode
  );

  /**
   * order.assignedDepartureLocation의 AI 자동기입 값을 _prefillAddress 키로 전달한다.
   *
   * counsel-start-flow 문서의 각 질문에 initialValueFrom 설정 시 자동으로 pre-fill 된다.
   *   address    → initialValueFrom: '_prefillAddress'
   *   dong       → initialValueFrom: '_prefillAddress.dong'
   *   ho(unit)   → initialValueFrom: '_prefillAddress.unit'
   *   floor      → initialValueFrom: '_prefillAddress.floor'
   *
   * 계산은 `buildDeparturePrefill` 에 있다. v2 문서의 DataSource 가 같은 함수를 derive 로
   * 부르므로, 두 경로의 prefill 이 갈라지지 않는다.
   */
  const startInitialValues = useMemo(
    (): CounselStartFlowContext | undefined =>
      buildDeparturePrefill(data?.order?.assignedDepartureLocation),
    [data?.order?.assignedDepartureLocation]
  );

  const counsel = data?.counsel ?? null;

  const { markPending, clearPending } = useObservingSessionPendingActions();

  const buildRefetch = useCallback(async () => {
    const detail = await refetch();
    return { counsel: detail?.counsel ?? null };
  }, [refetch]);

  useResumePendingObservingVideoUploads({
    visitCounselId,
    enabled:
      isAuthInitialized &&
      !isAuthLoading &&
      !!visitCounselId &&
      !!plannerId &&
      isPlannerMode &&
      !!counsel,
    showToast,
    refetch: buildRefetch,
    markPending,
    clearPending,
  });

  /**
   * counsel 스냅샷 → 현재 보여줄 단계.
   * 어댑터 콜백 후 refetch가 새 counsel을 가져오면 자동으로 재계산된다.
   */
  const stage: CounselFlowStage = useMemo(() => resolveCounselFlowStage(counsel), [counsel]);

  const isPostProgressStage = stage.kind === 'sketch-video' || stage.kind === 'additional-spaces';

  /**
   * post-progress 단계(`sketch-video` / `additional-spaces`) 진입 시 "상담 현황 리뷰" 화면을
   * 먼저 보여주고, 사용자가 CTA "다음" 을 눌러야 실제 촬영 어댑터로 진입하도록 인터셉트한다.
   *
   * ### 로컬 state 대신 intake.status 에서 파생하는 이유
   * 로컬 state 방식은 뒤로가기 후 재진입 시 컴포넌트가 unmount → remount 되어 state 가
   * 초기화된다. 이 때 SWR 캐시가 잠깐 stale(intake=STARTED) 상태이면 사용자가 "다음" 을
   * 누를 때 `completeIntakeSession` 이 중복 호출되어 API 오류가 발생했다.
   *
   * intake.status === 'COMPLETED' 는 "이미 리뷰 화면을 통과해 API 를 호출했다"는 불변 조건이므로
   * 이를 파생값으로 사용하면 중복 호출이 구조적으로 불가능해진다.
   */
  const reviewedPostProgress = useMemo(
    () => isPostProgressStage && counsel?.sessions?.intake?.status === 'COMPLETED',
    [isPostProgressStage, counsel]
  );

  /** post-progress 리뷰 CTA "다음" — `completeIntakeSession` 호출 중 로딩 표시용 */
  const [isCompletingIntake, setIsCompletingIntake] = useState(false);

  // ── 세션 편집 상태 ────────────────────────────────────────────────────────
  // 방/추가공간: EditTargetSelector → EditObservingFlow(MovingItemsFlowAdapter) 인라인 렌더.
  // intake 섹션: ProgressFlowAdapter 인라인 렌더.
  interface EditingSession {
    sessionId: string;
    isAdditional: boolean;
    roomLabel: string;
    initialMovingItems?: MovingItemRequest[];
    canRecordMovingItems?: boolean;
  }
  const [editingSession, setEditingSession] = useState<EditingSession | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [editingProgressSub, setEditingProgressSub] = useState<ProgressSubStage | null>(null);

  /**
   * 상담 현황 리뷰 화면이 사용하는 movingTypeSpecs (옛 `CounselProgressPage` 와 동일 SWR key/소스).
   * post-progress 또는 done(미완료 잔여) stage 진입 시 한 번만 fetch 된다.
   */
  const needsReviewSpecs = isPostProgressStage || stage.kind === 'done';
  const { data: movingTypeSpecs } = useSWR(
    isAuthInitialized && !isAuthLoading && !!plannerId && isPlannerMode && needsReviewSpecs
      ? 'moving-type-specs'
      : null,
    () => myVisitCounselApi.getMovingTypeSpecs(),
    { revalidateOnFocus: false }
  );

  /**
   * 어댑터 완료 콜백 — 어댑터 내부에서 이미 refetch까지 마친 counsel이 도착하므로
   * 페이지는 추가 작업 없이 stage 재평가에 의해 자동 전환된다.
   *
   * (기존 advanceCounselFlowAfterStep로 다음 페이지 URL로 이동하던 흐름은 더 이상 사용하지 않는다.)
   */
  const handleStageAdvance = useCallback(() => {
    // intentionally empty — useMyVisitCounselDetail 캐시가 새 counsel을 push하면 stage가 갱신됨
  }, []);

  // ── done 처리 ─────────────────────────────────────────────────────────────
  // 모든 진행 sub-stage 가 끝나면 두 갈래로 분기한다.
  //   1) "상담 완료 조건" (스케치 영상·옵저빙 영상·서비스셋 등 모두 충족) 이 만족되면
  //      `CounselCompleteScreen` 으로 자동 종료 + 축하 화면을 표시한다 (render 분기).
  //   2) 충족되지 않으면 `CounselReviewScreen` 으로 상담 현황을 보여주며 — 사용자가 직접
  //      "상담 상세에서 마무리하기" CTA 또는 좌상단 뒤로가기로 상세로 이동한다.
  //      (옛 동작: 토스트 + auto-navigate. 자동 이동이 "튕김" 으로 인지되어 제거.)
  const isReadyForCompletion = useMemo(() => isCounselReadyForCompletion(counsel), [counsel]);

  // ── 어댑터 공통 props ─────────────────────────────────────────────────────

  /**
   * HouseStructureFlowAdapter 전용 완료 콜백.
   *
   * 백엔드 eventual consistency 로 인해 POST 직후 GET 이 sketching 세션을 누락한
   * stale 데이터를 반환하는 경우가 있다 (특히 프로덕션).
   *
   *   - 최대 약 45초간 지수 백오프(300ms→2s) 폴링 — 아웃박스→프로젝션 발행 지연(측정상
   *     수십 초까지 스파이크)을 견디도록 창을 넉넉히 두되 폴링 부하는 백오프로 억제
   *   - 폴링 동안 어댑터의 `await onAfterDone()` 이 pending → CTA "다음" 은 "저장 중..." 유지
   *   - 폴링 성공: GET 결과를 SWR 에 반영 → stage 전환 → 어댑터 unmount
   *   - 예산 초과 시 POST 응답(updatedCounsel)에 stage 를 바꿀 수 있는 유효 sketching 이
   *     있으면 fallback 으로 반영, 없으면 안내 토스트 후 throw 해서 "저장 중..." 을 reset.
   *     (POST 는 멱등 — 재요청 시 409→현재 상태 반환 — 하므로 "다음" 재클릭이 안전한 재시도)
   *
   * "유효 sketching" 의 정의는 `resolveCounselFlowStage` 의 `hasSketchingData` 와 동일하다:
   *   sketching 세션 자체 존재 + (status=STARTED|COMPLETE 또는 rooms 길이 > 0).
   * 이 조건을 만족하지 않으면 mutate 해도 stage 가 'house-structure' 에서 벗어나지
   * 못해 사용자가 동일 화면에 갇히므로 mutate 하지 않고 재시도를 유도한다.
   */
  const handleHouseStructureAdvance = useCallback(
    async (updatedCounsel: VisitCounselView) => {
      const hasUsableSketching = (c: VisitCounselView): boolean => {
        const s = c.sessions?.sketching;
        if (!s) return false;
        const status = s.status;
        const roomsLen = s.rooms?.length ?? 0;
        return status === 'STARTED' || status === 'COMPLETE' || roomsLen > 0;
      };

      // 세션 프로젝션은 아웃박스→프로젝션 파이프라인을 거치는 eventual-consistent 읽기모델이라
      // 서버 부하 시 이벤트 발행이 수십 초까지 밀릴 수 있다(측정치: SketchingSession.Started 발행이
      // 평균 30초·최대 74초인 구간 존재). 과거엔 총 10초(250ms×40)만 폴링 후 조용히 실패해
      // 화면이 house-structure에 갇히고 새로고침 시 시작화면으로 되돌아갔다.
      //   - 총 대기 예산을 넉넉히(약 45초) 두고 지수 백오프로 폴링 부하를 줄인다.
      //   - 서버 반영이 확인되면 즉시 mutate → stage 전환 → 어댑터 unmount.
      //   - 예산을 넘겨도 POST는 이미 성공(멱등: 재요청 시 409→현재 상태 반환)했으므로,
      //     사용자에게 "저장됐고 반영 대기 중 — 다시 눌러주세요"를 명확히 안내하고 throw 하여
      //     버튼을 되돌린다. 재요청은 안전하며 그 사이 프로젝션이 대개 따라잡는다.
      const POLL_BUDGET_MS = 45_000;
      const POLL_MIN_INTERVAL_MS = 300;
      const POLL_MAX_INTERVAL_MS = 2_000;
      const deadline = Date.now() + POLL_BUDGET_MS;
      let interval = POLL_MIN_INTERVAL_MS;
      while (Date.now() < deadline) {
        try {
          const fresh = await myVisitCounselApi.getMyVisitCounselById(visitCounselId);
          if (hasUsableSketching(fresh)) {
            mutateWithCounsel(fresh);
            return;
          }
        } catch {
          // 일시 네트워크 오류 등은 무시하고 다음 attempt 진행
        }
        if (Date.now() + interval >= deadline) break;
        await new Promise<void>((r) => setTimeout(r, interval));
        interval = Math.min(POLL_MAX_INTERVAL_MS, Math.round(interval * 1.4));
      }

      if (hasUsableSketching(updatedCounsel)) {
        mutateWithCounsel(updatedCounsel);
        return;
      }

      showToast(
        '방 구조는 저장됐어요. 서버 반영이 지연되고 있으니 "다음"을 한 번 더 눌러주세요',
        'warning'
      );
      throw new Error('house-structure sync timeout');
    },
    [visitCounselId, mutateWithCounsel, showToast]
  );

  /**
   * 상담 현황 리뷰 CTA "다음" — intake 세션을 COMPLETED 로 만든 뒤 reviewedPostProgress 가
   * true 로 바뀌어 sketch-video / additional-spaces 어댑터로 전환되도록 한다.
   *
   * HouseStructureFlowAdapter 와 동일한 폴링 패턴 적용:
   *   - POST 직후 SWR 즉시 갱신은 하지 않는다 (stale GET 으로 stage flicker 방지)
   *   - GET 을 250ms × 40회 (최대 10초) 직접 폴링해 intake.status === 'COMPLETED' 확인 후 mutate
   *   - 폴링 동안 isCompletingIntake=true 유지 → 버튼 "진행 중..." 유지
   *   - 폴링 성공: SWR 갱신 → reviewedPostProgress=true → 다음 어댑터로 전환 (컴포넌트 갱신)
   *   - 폴링 실패 + POST 응답이 이미 COMPLETED 면 그 응답으로 mutate
   *   - 둘 다 안 되면 토스트 후 isCompletingIntake reset 으로 재시도 허용
   */
  const handlePostProgressProceed = useCallback(async () => {
    if (isCompletingIntake) return;
    if (counsel?.sessions?.intake?.status === 'COMPLETED') return;
    setIsCompletingIntake(true);
    try {
      // intake 세션이 아직 STARTED 가 아니면 먼저 시작 (race / 누락 대응)
      const intakeStatus = counsel?.sessions?.intake?.status;
      if (intakeStatus !== 'STARTED') {
        try {
          await myVisitCounselApi.startIntakeSession(visitCounselId);
        } catch {
          // 이미 STARTED 인 race condition 등은 무시하고 complete 진행
        }
      }

      const updatedCounsel = await myVisitCounselApi.completeIntakeSession(visitCounselId);

      const POLL_ATTEMPTS = 40;
      const POLL_INTERVAL_MS = 250;
      let confirmed = false;
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
        try {
          const fresh = await myVisitCounselApi.getMyVisitCounselById(visitCounselId);
          if (fresh.sessions?.intake?.status === 'COMPLETED') {
            mutateWithCounsel(fresh);
            confirmed = true;
            break;
          }
        } catch {
          // 일시 오류는 무시하고 다음 attempt
        }
        if (attempt < POLL_ATTEMPTS - 1) {
          await new Promise<void>((r) => setTimeout(r, POLL_INTERVAL_MS));
        }
      }

      if (!confirmed) {
        if (updatedCounsel?.sessions?.intake?.status === 'COMPLETED') {
          mutateWithCounsel(updatedCounsel);
        } else {
          showToast('서버 반영이 지연되고 있어요. 다시 시도해주세요', 'warning');
        }
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : '상담 진행 완료 처리에 실패했습니다', 'error');
    } finally {
      // 성공 시에는 reviewedPostProgress=true 로 인해 다음 렌더에서 CounselReviewScreen 이
      // 다른 어댑터로 교체되므로 reset 으로 인한 깜빡임이 보이지 않는다.
      setIsCompletingIntake(false);
    }
  }, [counsel, isCompletingIntake, visitCounselId, mutateWithCounsel, showToast]);

  /**
   * pick-room 단계의 좌상단 `<` 뒤로가기 — 항상 상담 상세 페이지로 이동한다.
   *
   * 기본 `handleBack` (`useCounselBackNavigation`) 은 탭 히스토리(canGoBack)이
   * 있으면 `goback()` 으로 직전 화면으로 돌아가는데, 상담 진행 도중 여러 단계가
   * 스택에 쌓인 경우 이전 화면이 다른 어댑터(짐 기록 등)일 수 있다. picker 화면의
   * 의도는 "상담 진행을 잠시 중단하고 상담 상세로 복귀" 이므로, 항상 상담 상세
   * 경로로 이동하도록 명시적으로 navigateTab 한다.
   */
  const navigateToCounselDetail = useCallback(() => {
    navigateTab(
      deserialize(`/workspace/my/counsels/${visitCounselId}${tabId ? `?tabId=${tabId}` : ''}`)
    );
  }, [navigateTab, visitCounselId, tabId]);

  /**
   * 리뷰 화면 → intake 섹션 수정 → ProgressFlowAdapter (신규 InquiryFlow 기반)
   *
   * 어느 섹션이 어느 화면을 여는지는 `REVIEW_EDIT_ROUTES` 선언에 있다. 페이지와 v2 문서가
   * 같은 테이블을 보므로 두 경로의 라우팅이 갈라지지 않는다.
   */
  const handleEditIntakeSection = useCallback((section: ReviewSection) => {
    setEditingProgressSub(reviewEditSubStage(section) as ProgressSubStage);
  }, []);

  /** 리뷰 화면 → 방 세션 수정 (EditTargetSelector → EditObservingFlow) */
  const handleEditRoom = useCallback((info: ReviewEditRoomInfo) => {
    setEditingSession({
      sessionId: info.sessionId,
      isAdditional: false,
      roomLabel: info.roomLabel,
      initialMovingItems: info.initialMovingItems,
    });
    setEditTarget(null);
  }, []);

  /** 리뷰 화면 → 추가공간 세션 수정 (EditTargetSelector → EditObservingFlow) */
  const handleEditAdditionalSpace = useCallback((info: ReviewEditAdditionalSpaceInfo) => {
    setEditingSession({
      sessionId: info.sessionId,
      isAdditional: true,
      roomLabel: info.spaceLabel,
      initialMovingItems: info.initialMovingItems,
      canRecordMovingItems: info.canRecordMovingItems,
    });
    setEditTarget(null);
  }, []);

  /** 세션 편집 완료 */
  const handleEditSessionDone = useCallback(
    (updatedCounsel: VisitCounselView) => {
      mutateWithCounsel(updatedCounsel);
      setEditingSession(null);
      setEditTarget(null);
    },
    [mutateWithCounsel]
  );

  /** 세션 편집 뒤로가기 */
  const handleEditSessionBack = useCallback(() => {
    if (editTarget !== null) {
      setEditTarget(null);
    } else {
      setEditingSession(null);
    }
  }, [editTarget]);

  /** ProgressFlowAdapter 편집 완료/취소 */
  const handleEditProgressDone = useCallback(() => {
    setEditingProgressSub(null);
  }, []);

  // ── 완료 화면 CTA navigations ────────────────────────────────────────────
  const tabQuery = tabId ? `?tabId=${tabId}` : '';
  const navigateToNextCounsel = useCallback(
    (nextId: string) => {
      navigateTab(deserialize(`/workspace/my/counsels/${nextId}${tabQuery}`));
    },
    [navigateTab, tabQuery]
  );
  const navigateToSchedule = useCallback(() => {
    navigateTab(deserialize(`/workspace/my/counsels${tabQuery}`));
  }, [navigateTab, tabQuery]);

  return (
    <CounselPageShell
      tabContext={props}
      visitCounselId={visitCounselId}
      error={error?.message}
      isLoading={isLoading || !data}
      fallbackPath={`/workspace/my/counsels/${visitCounselId}${props.tabId ? `?tabId=${props.tabId}` : ''}`}
      renderLoading={() => <CounselLoadingPlaceholder />}
    >
      {({ handleBack, showToast }) => {
        // ⚠️ showToast 는 Shell 이 렌더하는 Toast 와 동일 인스턴스인 ctx.showToast 를 쓴다.
        //    (페이지 상단 useCounselPageContext 의 showToast 는 별도 인스턴스라 화면에 안 뜬다 —
        //     아래 플로우 어댑터들은 반드시 이 ctx.showToast 로 토스트를 띄워야 한다.)
        // ── 리뷰에서 방/추가공간 "수정" 클릭 → EditTargetSelector → EditObservingFlow ──
        if (editingSession) {
          if (editTarget === null) {
            return (
              <EditTargetSelector
                roomLabel={editingSession.roomLabel}
                onSelect={setEditTarget}
                onBack={handleEditSessionBack}
              />
            );
          }
          return (
            <EditObservingFlow
              key={`${editingSession.sessionId}-${editTarget}`}
              visitCounselId={visitCounselId}
              sessionId={editingSession.sessionId}
              isAdditionalSpaceMoving={editingSession.isAdditional}
              roomLabel={editingSession.roomLabel}
              initialMovingItems={editingSession.initialMovingItems}
              editTarget={editTarget}
              canRecordMovingItems={editingSession.canRecordMovingItems}
              showToast={showToast}
              refetch={buildRefetch}
              onAfterDone={handleEditSessionDone}
              onBack={handleEditSessionBack}
            />
          );
        }

        // ── 리뷰에서 intake 섹션 "수정" 클릭 → ProgressFlowAdapter ──────────
        if (editingProgressSub && counsel) {
          return (
            <ProgressFlowAdapter
              orderMoveDate={data?.order?.moving?.moveDate ?? null}
              key={editingProgressSub.kind}
              visitCounselId={visitCounselId}
              counsel={counsel}
              sub={editingProgressSub}
              showToast={showToast}
              refetch={buildRefetch}
              onAfterDone={handleEditProgressDone}
              onCancel={handleEditProgressDone}
            />
          );
        }

        if (stage.kind === 'done') {
          if (isReadyForCompletion && counsel) {
            return (
              <CounselCompleteScreen
                visitCounselId={visitCounselId}
                counsel={counsel}
                plannerId={plannerId ?? ''}
                showToast={showToast}
                refetch={buildRefetch}
                onNavigateToNextCounsel={navigateToNextCounsel}
                onNavigateToSchedule={navigateToSchedule}
              />
            );
          }
          // 진행 시퀀스(이사일~고객요청)는 끝났지만 완료 조건(스케치 영상·방 영상·serviceSet 등)이
          // 남아있는 경우 — 자동 navigate 없이 현재 상담 현황을 풀스크린으로 보여 사용자가
          // 직접 상담 상세로 이동해 마무리하도록 한다. (옛 동작: 토스트 + auto-bounce → 사용자가
          // "튕김" 으로 인지하고 세션 재진입 시 또 동일 bounce 가 일어나 진행 불가했던 회귀 수정.)
          if (counsel) {
            return (
              <CounselReviewScreen
                counsel={counsel}
                sketchingSession={counsel.sessions?.sketching ?? null}
                roomObserving={counsel.sessions?.roomObserving ?? []}
                additionalSpaceObserving={counsel.sessions?.additionalSpaceObserving ?? []}
                movingTypeSpecs={movingTypeSpecs ?? null}
                onProceed={navigateToCounselDetail}
                onBack={navigateToCounselDetail}
                proceedLabel="상담 상세에서 마무리하기"
                onEditIntakeSection={handleEditIntakeSection}
                onEditRoom={handleEditRoom}
                onEditAdditionalSpace={handleEditAdditionalSpace}
              />
            );
          }
          return <CounselLoadingPlaceholder />;
        }

        if (stage.kind === 'house-structure') {
          return (
            <HouseStructureFlowAdapter
              visitCounselId={visitCounselId}
              showToast={showToast}
              mutateWithCounsel={mutateWithCounsel}
              onAfterDone={handleHouseStructureAdvance}
              onCancel={handleBack}
            />
          );
        }

        if (stage.kind === 'moving-items') {
          const { target } = stage;
          return (
            <MovingItemsFlowAdapter
              key={target.sessionId}
              visitCounselId={visitCounselId}
              sessionId={target.sessionId}
              isAdditionalSpaceMoving={target.isAdditionalSpaceMoving}
              isFirstStart={target.isFirstStart}
              roomLabel={target.roomLabel}
              roomType={target.roomType}
              initialMovingItems={target.initialMovingItems}
              showToast={showToast}
              refetch={buildRefetch}
              onAfterDone={handleStageAdvance}
              onCancel={handleBack}
            />
          );
        }

        if (stage.kind === 'pick-room' && counsel) {
          return (
            <RoomObservingFlowAdapter
              visitCounselId={visitCounselId}
              counsel={counsel}
              showToast={showToast}
              refetch={buildRefetch}
              onAfterDone={handleStageAdvance}
              onCancel={navigateToCounselDetail}
            />
          );
        }

        if (stage.kind === 'progress' && counsel) {
          return (
            <ProgressFlowAdapter
              orderMoveDate={data?.order?.moving?.moveDate ?? null}
              key={stage.sub.kind}
              visitCounselId={visitCounselId}
              counsel={counsel}
              sub={stage.sub}
              showToast={showToast}
              refetch={buildRefetch}
              onAfterDone={handleStageAdvance}
              onCancel={navigateToCounselDetail}
            />
          );
        }

        if (isPostProgressStage && counsel) {
          // 1) 사용자가 아직 post-progress "상담 현황 리뷰" 를 확인하지 않았으면 풀스크린 리뷰부터 노출.
          //    CTA "다음" → `completeIntakeSession` API 호출 → 백엔드가 운반방식 기반으로
          //    추가공간 세션 자동 생성 → refetch → reviewedPostProgress=true → 다음 stage(sketch-video
          //    또는 additional-spaces) 어댑터로 진입. sketch-video → additional-spaces 가 연속되어도
          //    review 는 한 번만 본다 (state 가 true 로 유지됨).
          if (!reviewedPostProgress) {
            return (
              <CounselReviewScreen
                counsel={counsel}
                sketchingSession={counsel.sessions?.sketching ?? null}
                roomObserving={counsel.sessions?.roomObserving ?? []}
                additionalSpaceObserving={counsel.sessions?.additionalSpaceObserving ?? []}
                movingTypeSpecs={movingTypeSpecs ?? null}
                onProceed={handlePostProgressProceed}
                onBack={navigateToCounselDetail}
                proceedLabel="다음"
                isProceeding={isCompletingIntake}
                onEditIntakeSection={handleEditIntakeSection}
                onEditRoom={handleEditRoom}
                // 추가공간은 이 리뷰 이후 completeIntakeSession 으로 생성되므로 수정 불가
              />
            );
          }

          // 2) 리뷰 확인 후 — 현재 stage 에 맞는 어댑터.
          if (stage.kind === 'sketch-video') {
            // 스케치 영상 촬영 → onAfterDone 후 counsel refetch → stage 가 자연스럽게
            // additional-spaces 또는 done 으로 전환된다.
            return (
              <SketchVideoStepAdapter
                visitCounselId={visitCounselId}
                showToast={showToast}
                refetch={buildRefetch}
                onAfterDone={handleStageAdvance}
                onCancel={navigateToCounselDetail}
              />
            );
          }

          // additional-spaces — 미완료 추가공간 picker + nested MovingItemsFlowAdapter.
          // 모든 추가공간 완료 시 onAfterDone → counsel refetch → stage='done' 으로 자동 전환.
          return (
            <AdditionalSpaceFlowAdapter
              visitCounselId={visitCounselId}
              counsel={counsel}
              showToast={showToast}
              refetch={buildRefetch}
              onAfterDone={handleStageAdvance}
              onCancel={navigateToCounselDetail}
            />
          );
        }

        return (
          <CounselStartFlowAdapter
            visitCounselId={visitCounselId}
            showToast={showToast}
            refetch={buildRefetch}
            mutateWithCounsel={mutateWithCounsel}
            onAfterStarted={handleStageAdvance}
            onCancel={handleBack}
            initialValues={startInitialValues}
          />
        );
      }}
    </CounselPageShell>
  );
};

export default CounselStartPage;
